import { Editor, Element, Node, Path } from 'slate';
import { isTag } from './navigation';
import { TagElement, TagEntry } from '../types';

interface ChainLink {
  tagId?: string;
  continuedAt?: string;
}

const BLOCK_CARRIER_TYPES = new Set(['reference-table', 'image']);

const chainLinks = (n: Node): ChainLink[] => {
  if (isTag(n)) return [{ tagId: (n as TagElement).tagId, continuedAt: (n as TagElement).continuedAt }];
  if (Element.isElement(n) && BLOCK_CARRIER_TYPES.has((n as { type?: string }).type as string)) {
    const tags = (n as { tags?: any[] }).tags;
    if (Array.isArray(tags)) return tags.map((b: any) => ({ tagId: b?.tagId, continuedAt: b?.continuedAt }));
  }
  return [];
};

const matchesAnyByTagId = (n: Node, tagId: string): boolean => chainLinks(n).some((l) => l.tagId === tagId);

const matchesPredecessorByTargetId = (n: Node, targetId: string): boolean => chainLinks(n).some((l) => l.continuedAt === targetId);

const getContinuedAtForTagId = (node: Node, tagId: string): string | undefined => chainLinks(node).find((l) => l.tagId === tagId)?.continuedAt ?? undefined;

const getPredecessorTagId = (node: Node, targetId: string): string | undefined => chainLinks(node).find((l) => l.continuedAt === targetId)?.tagId;

/**
 * Get the tag that continues into this element (the previous tag in the chain)
 */
export const getContinuedFrom = (editor: Editor, element: TagElement): TagElement | null => {
  const found = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => isTag(n) && n.continuedAt === element.tagId,
    })
  );
  return found.length ? (found[0][0] as TagElement) : null;
};

/**
 * Get the tag that this element continues to (the next tag in the chain)
 */
export const getContinuedTo = (editor: Editor, element: TagElement): TagEntry | null => {
  if (!element.continuedAt) return null;
  const found = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => isTag(n) && n.tagId === element.continuedAt,
    })
  );
  return found.length ? { node: found[0][0] as TagElement, path: found[0][1] } : null;
};

export interface ChainEntry {
  node: TagElement | Element;
  path: Path;
  tagId: string;
}

/**
 * Get the entire continuation chain for a tag.
 */
export const getContinuationChain = (editor: Editor, tagId: string): ChainEntry[] => {
  const chain: ChainEntry[] = [];
  const visited = new Set<string>();

  // Find the root (first tag in the chain)
  let currentId: string | undefined = tagId;
  let rootEntry: ChainEntry | null = null;

  // Walk backwards to find the root
  while (currentId && !visited.has(currentId)) {
    const idToFind: string = currentId;
    visited.add(idToFind);
    const found = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) => matchesAnyByTagId(n, idToFind),
      })
    )[0];

    if (!found) break;
    rootEntry = { node: found[0] as Element, path: found[1] as Path, tagId: idToFind };

    // Find a predecessor — anything whose chain link points at `idToFind`.
    const predecessor = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) => matchesPredecessorByTargetId(n, idToFind),
      })
    )[0];

    if (predecessor) {
      const predecessorTagId = getPredecessorTagId(predecessor[0], idToFind);
      if (!predecessorTagId) break;
      currentId = predecessorTagId;
    } else {
      break;
    }
  }

  // Now walk forward from root to build the chain
  if (rootEntry) {
    visited.clear();
    let current: ChainEntry | null = rootEntry;
    while (current && !visited.has(current.tagId)) {
      visited.add(current.tagId);
      chain.push(current);

      const continuedAtId = getContinuedAtForTagId(current.node, current.tagId);
      if (continuedAtId) {
        const next = Array.from(
          Editor.nodes(editor, {
            at: [],
            match: (n) => matchesAnyByTagId(n, continuedAtId),
          })
        )[0];
        current = next ? { node: next[0] as Element, path: next[1] as Path, tagId: continuedAtId } : null;
      } else {
        current = null;
      }
    }
  }

  return chain;
};

/**
 * Get the combined text content of an entire continuation chain
 */
export const getContinuationChainText = (editor: Editor, tagId: string): string => {
  const chain = getContinuationChain(editor, tagId);
  return chain.map(({ node }) => Node.string(node)).join('');
};

/**
 * Check if a tag is part of a continuation chain
 */
export const isPartOfContinuation = (editor: Editor, element: TagElement): boolean => {
  return !!element.continuedAt || getContinuedFrom(editor, element) !== null;
};
